import { Portal as TooltipPortal } from "@radix-ui/react-tooltip";
import * as Dialog from "@radix-ui/react-dialog";
import "./idea-dialog.css";
import { useState, useEffect, useMemo } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../services/Firebase";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Badge } from "../ui/badge";
import {
  X,
  Sparkles,
  Archive,
  ArchiveRestore,
  ExternalLink,
  Rss,
  List,
  Quote,
  Eye,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

import { ProposalBrandAsset, proposalSlides } from "../../lib/postProposals";
import { IdeaCardMaterials, IdeaMoodBoard, IdeaSourceHeader, IdeaSourceLine } from "./IdeaMoodBoard";

interface CategorizedContent {
  category: "listicle" | "quote";
  captionText: string;
  templateTags: string[];
  content: any;
  titleSlideImage?: any;
  assignedImage?: any;
}

export interface PendingTopicData {
  id: string;
  sourceId: string;
  sourceName: string;
  articleUrl: string;
  articleTitle: string;
  topicIndex: number;
  content: CategorizedContent;
  status: "pending" | "generated" | "archived";
  postId?: string;
  articlePublishedAt?: number;
  discoveredAt: number;
  generatedAt?: number;
  extractedArticleImages?: { storagePath?: string }[];
}

interface Props {
  /** Overrides the source's stored name, e.g. a website's domain. */
  sourceLabel?: string;
  compact?: boolean;
  preview?: boolean;
  previewOpen?: boolean;
  onPreviewOpenChange?: (expanded: boolean) => void;
  topic: PendingTopicData;
  displayTab: string;
  onGenerate: (topicId: string) => Promise<{ postId: string }>;
  onConfigure?: (topic: PendingTopicData) => void;
  hasLastSettings?: boolean;
  onArchive: (topicId: string) => Promise<void>;
  onUnarchive: (topicId: string) => Promise<void>;
  onViewPost?: (postId: string) => void;
  /** Brand kit images, so previews can show the photos chosen for each slide. */
  brandAssets?: ProposalBrandAsset[];
  /** Set when drafting is not possible (no credits); previews offer this instead. */
  onUpgrade?: () => void;
}

export function TopicCard({
  sourceLabel,
  compact = false,
  preview = false,
  previewOpen = false,
  onPreviewOpenChange,
  topic,
  displayTab,
  onGenerate,
  onConfigure,
  hasLastSettings = false,
  onArchive,
  onUnarchive,
  onViewPost,
  brandAssets,
  onUpgrade,
}: Props) {
  const [generating, setGenerating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPostId, setGeneratedPostId] = useState<string | null>(null);
  const [postReady, setPostReady] = useState(false);
  const [postMissing, setPostMissing] = useState(false);
  const [generationRequestInFlight, setGenerationRequestInFlight] =
    useState(false);

  useEffect(() => {
    if (topic.status !== "generated") {
      setGeneratedPostId(null);
      setGenerating(false);
      setPostReady(false);
      setPostMissing(false);
      setGenerationRequestInFlight(false);
      setError(null);
      return;
    }

    if (topic.postId && generatedPostId === topic.postId) {
      setGeneratedPostId(null);
    }

    if (!topic.postId) {
      setGenerating(false);
      setPostReady(false);
    }

    setPostMissing(false);
  }, [topic.status, topic.postId, generatedPostId]);

  // Listen to generated post's Firestore doc for status changes
  useEffect(() => {
    const postId =
      generatedPostId || (generationRequestInFlight ? null : topic.postId);
    if (!postId) return;
    if (topic.status !== "generated" && !generating) return;

    const unsubscribe = onSnapshot(
      doc(db, "posts", postId),
      (snap) => {
        if (!snap.exists()) {
          setGenerating(false);
          setPostReady(false);
          setPostMissing(true);
          return;
        }

        setPostMissing(false);
        const status = snap.data().status;
        const generationError = snap.data().generationError as
          | string
          | null
          | undefined;

        if (status === "failed") {
          setGenerating(false);
          setPostReady(false);
          setError(generationError || "Generation failed");
          return;
        }

        if (status === "generating" || status === "pre-processing") {
          setPostReady(false);
          return;
        }

        if (status) {
          setPostReady(true);
          setGenerating(false);
          setError(null);
        }
      },
      (err) => console.error("Post listener error:", err),
    );
    return () => unsubscribe();
  }, [
    generatedPostId,
    topic.postId,
    topic.status,
    generating,
    generationRequestInFlight,
  ]);

  const handleGenerate = async () => {
    if (generating || archiving) return;
    setGenerationRequestInFlight(true);
    setGenerating(true);
    setPostReady(false);
    setPostMissing(false);
    setError(null);
    try {
      const result = await onGenerate(topic.id);
      setGeneratedPostId(result.postId);
      setGenerationRequestInFlight(false);
    } catch (err: any) {
      setError(err.handled ? null : err.message || "Generation failed");
      setGenerating(false);
      setGenerationRequestInFlight(false);
    }
  };
  const generateAction = onConfigure
    ? () => onConfigure(topic)
    : handleGenerate;
  // "Draft this" is always one click (the feed falls back to default settings);
  // Customize is where settings are chosen. Cards without Customize keep
  // their single action.
  const designAction = onUpgrade
    ? onUpgrade
    : preview || hasLastSettings
      ? handleGenerate
      : generateAction;
  const draftLabel = onUpgrade ? "Upgrade to draft" : "Draft this";
  const customizeButton =
    onConfigure && !onUpgrade ? (
      <Button
        size="sm"
        variant="ghost"
        className="min-h-11 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground sm:min-h-9"
        disabled={generating || archiving || topic.status !== "pending"}
        onClick={() => onConfigure(topic)}
      >
        Customize
      </Button>
    ) : null;
  const slides = useMemo(
    () => (preview ? proposalSlides(topic, brandAssets || []) : []),
    [preview, topic, brandAssets],
  );
  const quickGenerateButton = hasLastSettings && onConfigure ? (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="min-h-11 shrink-0 px-2 text-xs text-muted-foreground hover:text-foreground sm:min-h-9"
            disabled={generating || archiving || topic.status !== "pending"}
            onClick={handleGenerate}
          >
            Quick Generate
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>Generate with your settings from last time.</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  const handleArchive = async () => {
    if (archiving || generating) return;
    setError(null);
    setArchiving(true);
    try {
      await onArchive(topic.id);
    } catch (err) {
      console.error("Archive failed:", err);
      setError("Couldn’t dismiss this idea. Please try again.");
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    setUnarchiving(true);
    try {
      await onUnarchive(topic.id);
    } catch (err) {
      console.error("Unarchive failed:", err);
      setUnarchiving(false);
    }
  };

  const getTitle = () => {
    if (topic.content.category === "listicle") {
      return topic.content.content.title;
    }
    if (topic.content.category === "quote") {
      const quote = topic.content.content.quote || "";
      return quote.length > 80 ? quote.substring(0, 80) + "\u2026" : quote;
    }
    return "Untitled Idea";
  };

  const getSnippet = () => {
    return topic.content.captionText;
  };

  const { timeAgo, timeLabel } = useMemo(() => {
    if (displayTab === "generated" && topic.generatedAt) {
      return {
        timeAgo: formatTimeAgo(topic.generatedAt),
        timeLabel: "Generated",
      };
    }
    const pubTime = topic.articlePublishedAt || topic.discoveredAt;
    return { timeAgo: formatTimeAgo(pubTime), timeLabel: "Published" };
  }, [
    displayTab,
    topic.generatedAt,
    topic.articlePublishedAt,
    topic.discoveredAt,
  ]);

  const [faviconError, setFaviconError] = useState(false);

  const faviconUrl = useMemo(() => {
    try {
      const hostname = new URL(topic.articleUrl).hostname;
      return `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
    } catch {
      return null;
    }
  }, [topic.articleUrl]);

  const activePostId =
    generatedPostId ||
    (generationRequestInFlight ? null : topic.postId) ||
    null;
  const showCrafting =
    !error &&
    !postMissing &&
    (generating ||
      (topic.status === "generated" && !!activePostId && !postReady));
  const showReady =
    topic.status === "generated" && postReady && !generating && !postMissing;
  const showGeneratedActions = topic.status === "generated" && !showCrafting;

  const categoryAccent =
    topic.content.category === "listicle"
      ? "border-l-blue-400"
      : "border-l-amber-400";

  const previewButton = (
    <button
      type="button"
      aria-label={`Preview suggested post: ${getTitle()}`}
      className="flex w-full flex-col gap-3 rounded-t-lg p-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <IdeaSourceHeader
        topic={topic}
        label={sourceLabel}
        timeAgo={formatTimeAgo(
          topic.status === "generated" && topic.generatedAt
            ? topic.generatedAt
            : topic.articlePublishedAt || topic.discoveredAt,
        )}
      />
      <IdeaCardMaterials topic={topic} />
      {generating && (
        <span role="status" className="text-xs text-muted-foreground">
          Drafting your post…
        </span>
      )}
    </button>
  );

  const card = (
    <div
      className={
        preview
          ? "flex min-h-0 flex-1 flex-col"
          : compact
            ? "flex min-w-0 rounded-lg border border-border bg-card"
            : `border rounded-lg border-l-[3px] ${categoryAccent} bg-card transition-all hover:shadow-sm`
      }
    >
      <div
        className={
          preview
            ? "flex min-h-0 flex-1 flex-col"
            : compact
              ? "flex min-w-0 flex-1 flex-col p-5"
              : "p-4"
        }
      >
        <div
          className={
            preview ? "min-h-0 flex-1 overflow-y-auto" : "contents"
          }
        >
          {preview ? (
            // Same board as onboarding's first post: title, caption, photos, outline.
            <IdeaMoodBoard topic={topic} slides={slides} bare />
          ) : (
          <>
          {/* Header row: source + category + time */}
          <div
            className={
              compact
                ? "mb-4 flex flex-wrap items-center justify-between gap-y-2"
                : "flex items-center justify-between mb-2.5"
            }
          >
            <div className="flex items-center gap-2 min-w-0">
              {faviconUrl && !faviconError ? (
                <img
                  src={faviconUrl}
                  alt=""
                  className="h-4 w-4 rounded-sm shrink-0"
                  onError={() => setFaviconError(true)}
                />
              ) : (
                <Rss className="h-3.5 w-3.5 text-orange-500 shrink-0" />
              )}
              <span className="text-xs font-medium text-foreground/70 truncate">
                {topic.sourceName}
              </span>
              <Badge
                variant="secondary"
                className={`text-[10px] py-0 px-1.5 shrink-0 ${
                  topic.content.category === "listicle"
                    ? "bg-blue-50 text-blue-600 border-blue-100"
                    : "bg-amber-50 text-amber-600 border-amber-100"
                }`}
              >
                {topic.content.category === "listicle" ? (
                  <List className="h-2.5 w-2.5 mr-0.5" />
                ) : (
                  <Quote className="h-2.5 w-2.5 mr-0.5" />
                )}
                {topic.content.category === "listicle" ? "Listicle" : "Quote"}
              </Badge>
            </div>
            <span className="text-[11px] text-muted-foreground shrink-0 ml-2">
              {timeLabel} {timeAgo}
            </span>
          </div>

          {/* Title */}
          <h3
            className={
              compact
                ? "mb-3 text-base font-semibold leading-snug text-foreground"
                : "font-semibold text-[13px] text-foreground mb-1.5 leading-snug"
            }
          >
            {preview && topic.content.category === "quote"
              ? topic.content.content.quote
              : getTitle()}
          </h3>

          {/* Source article link */}
          <a
            href={topic.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2.5 max-w-full"
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate">{topic.articleTitle}</span>
          </a>

          {/* Snippet */}
          <p
            className={`text-xs text-muted-foreground leading-relaxed ${expanded || previewOpen ? "mb-3" : "line-clamp-2"}`}
          >
            {getSnippet()}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground/60 hover:text-foreground transition-colors mt-1 mb-3"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                More
              </>
            )}
          </button>

          </>
          )}

          {/* Error */}
          {error && (
            <div className={`text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5 mb-2.5${preview ? " mx-5 mt-4 sm:mx-6" : ""}`}>
              {error}
            </div>
          )}
        </div>
        {/* Actions */}
        <div
          className={
            preview
              ? "flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-background px-5 py-4 sm:px-6"
              : compact
                ? "mt-auto flex flex-wrap items-center gap-2 border-t border-border/50 pt-3"
                : "flex items-center gap-2 pt-1 border-t border-border/50"
          }
        >
          {topic.status === "pending" && !generating && (
            <>
              <Button
                size="sm"
                variant={preview ? "default" : compact ? "outline" : "default"}
                onClick={preview ? designAction : generateAction}
                className={
                  preview
                    ? "min-h-11 bg-brand text-brand-foreground hover:bg-brand/90"
                    : compact
                      ? "min-h-11 text-xs"
                      : "h-7 text-xs"
                }
              >
                <Sparkles className="h-3 w-3 mr-1" />
                {preview ? draftLabel : "Generate Visuals"}
              </Button>
              {preview ? customizeButton : quickGenerateButton}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleArchive}
                disabled={archiving}
                className={
                  preview
                    ? "min-h-11 text-sm text-muted-foreground"
                    : "h-7 text-xs text-muted-foreground"
                }
              >
                {archiving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Archive className="h-3 w-3 mr-1" />
                )}
                Dismiss
              </Button>
            </>
          )}

          {showCrafting && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
              <div className="h-4 w-4 rounded-full border-2 border-gray-200 border-t-[#FF5C35] animate-spin" />
              <span>Crafting your post...</span>
            </div>
          )}

          {showGeneratedActions && (
            <div className="flex items-center gap-2 py-0.5">
              {showReady ? (
                <div className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">Ready</span>
                </div>
              ) : postMissing ? (
                <Badge
                  variant="outline"
                  className="text-[11px] text-muted-foreground py-0 px-1.5"
                >
                  Post removed
                </Badge>
              ) : null}
              {showReady && activePostId && onViewPost && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onViewPost(activePostId)}
                  className={compact ? "min-h-11 text-xs" : "h-7 text-xs"}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View Post
                </Button>
              )}
              {activePostId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerate}
                  className={compact ? "min-h-11 text-xs" : "h-7 text-xs"}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Regenerate Visuals
                </Button>
              )}
            </div>
          )}

          {topic.status === "archived" && (
            <div className="flex items-center gap-2 py-0.5">
              <Badge
                variant="outline"
                className="text-[11px] text-muted-foreground py-0 px-1.5"
              >
                Archived
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleUnarchive}
                disabled={unarchiving}
                className={compact ? "min-h-11 text-xs" : "h-7 text-xs"}
              >
                {unarchiving ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <ArchiveRestore className="h-3 w-3 mr-1" />
                )}
                Restore
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  if (!preview) return card;

  return (
    <Dialog.Root open={previewOpen} onOpenChange={onPreviewOpenChange}>
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-background">
        <Dialog.Trigger asChild>{previewButton}</Dialog.Trigger>
        <IdeaSourceLine topic={topic} slides={slides} className="-mt-1 px-3 pb-3" />
        {error && (
          <p role="alert" className="px-3 pb-2 text-xs text-destructive">
            {error}
          </p>
        )}
        {/* Cards in a row share a height; spare space gathers above the actions,
            so content stays together and action bars line up. */}
        <div className="mt-auto flex flex-wrap items-center gap-1 border-t border-border/60 px-3 py-2">
          {/* The same card lists drafted and dismissed ideas in the full view. */}
          {topic.status === "generated" ? (
            showCrafting ? (
              <span role="status" className="flex min-h-11 items-center gap-2 px-1 text-xs text-muted-foreground sm:min-h-9">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Crafting your post…
              </span>
            ) : (
              <>
                {showReady && activePostId && onViewPost && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-11 shrink-0 gap-1.5 px-2.5 text-xs sm:min-h-9"
                    onClick={() => onViewPost(activePostId)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View post
                  </Button>
                )}
                {postMissing && (
                  <span className="px-1 text-xs text-muted-foreground">Post removed</span>
                )}
                {activePostId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-11 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground sm:min-h-9"
                    onClick={handleGenerate}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Regenerate
                  </Button>
                )}
              </>
            )
          ) : topic.status === "archived" ? (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 shrink-0 gap-1.5 px-2 text-xs sm:min-h-9"
              onClick={handleUnarchive}
              disabled={unarchiving}
            >
              {unarchiving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArchiveRestore className="h-3.5 w-3.5" />
              )}
              Restore
            </Button>
          ) : (
          <>
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 shrink-0 gap-1.5 px-2.5 text-xs sm:min-h-9"
            onClick={designAction}
            disabled={generating || archiving || topic.status !== "pending"}
          >
            {generating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {generating ? "Drafting…" : draftLabel}
          </Button>
          {customizeButton}
          <TooltipProvider delayDuration={250}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-11 w-9 shrink-0 text-muted-foreground sm:h-9"
                  aria-label={`Dismiss suggested post: ${getTitle()}`}
                  onClick={handleArchive}
                  disabled={
                    archiving || generating || topic.status !== "pending"
                  }
                >
                  {archiving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Archive className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>Dismiss suggested post</TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
          </>
          )}
        </div>
      </div>
      <Dialog.Portal>
        <Dialog.Overlay className="idea-dialog-overlay fixed inset-0 z-50 bg-foreground/30" />
        <Dialog.Content className="idea-dialog fixed inset-0 z-50 flex h-[100dvh] flex-col overflow-hidden bg-background shadow-xl focus:outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[85dvh] sm:w-[calc(100vw-3rem)] sm:max-w-3xl sm:rounded-xl">
          <header className="flex shrink-0 items-center justify-between border-b border-border px-5 py-2 sm:px-6">
            <Dialog.Title className="text-sm font-medium">
              Idea details
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11"
                aria-label="Close suggested post"
              >
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
            <Dialog.Description className="sr-only">
              Read the suggested post, generate visuals, or dismiss it.
            </Dialog.Description>
          </header>
          {card}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}
